<template>
  <Sheet
    :visible="visible"
    :title="$t('Account Notes')"
    :width="700"
    @close="visible = false"
    padded
  >
    <div id="AccountNotesModal">
      <Card>
        <Filters :query.sync="filter"/>
        <ScrollShadow>
          <Tabs
            :tabs="tabs"
            v-model="selected"
          />
        </ScrollShadow>
        <div class="Modal-notes">
          <div v-if="loading" class="Modal-notes-none">{{$t('Loading')}} ...</div>
          <div v-if="!loading && filteredNotes.length === 0" class="Modal-notes-none">{{$t('Nothing to see here')}} ...</div>
          <template v-if="filteredNotes.length">
            <transition-group :name="'slide'">
              <template v-for="note in filteredNotes">
                <div
                  :key="note.id"
                  class="note"
                >
                  <div class="head">
                    <span class="type">
                      <!-- <Icon :icon="getNoteTypeIcon(note)"/> -->
                      <router-link v-if="getObjectLink(note)" :to="getObjectLink(note)" tag="a" target="_blank">{{ getNoteTypeName(note) }}</router-link>
                      <span v-else>{{ getNoteTypeName(note) }}</span>
                      <div><Caption>{{ getNoteType(note) }}</Caption></div>
                    </span>
                    <div class="note-info">
                      <Formatter class="note-date" type="dateTimeShort" :value="note.createdOn" />
                      <div class="note-author">{{ note.createdByName }}</div>
                    </div>
                  </div>
                  <div class="details">
                    <template v-if="typeof note.notes === 'string'">
                      {{parseSimpleNote(note.objectType, note.notes)}}
                    </template>
                    <template v-else-if="note.objectType === 'CONVERSIONDEFINITION' && note.objectAction === 'CREATE'">
                      {{$t('AccountNotes.addedConversionDefinition')}}
                      <div class="inner-stats">
                        <Stats class="inner-stats">
                          <Stat :label="$t('ID')" :value="note.notes && note.notes.id"/>
                          <Stat :label="$t('Name')" :value="note.notes && note.notes.name"/>
                          <Stat :label="$t('ConversionDefinitions.ruleX', { value: 1 })" :value="getConversionRuleLabel(note.notes && note.notes.action1Type)"/>
                          <Stat :label="$t('ConversionDefinitions.ruleXReference', { value: 1 })" :value="note.notes && note.notes.action1Reference"/>
                          <Stat v-if="note.notes && note.notes.action2Type" :label="$t('ConversionDefinitions.ruleX', { value: 2 })" :value="getConversionRuleLabel(note.notes && note.notes.action2Type)"/>
                          <Stat v-if="note.notes && note.notes.action2Type" :label="$t('ConversionDefinitions.ruleXReference', { value: 2 })" :value="note.notes && note.notes.action2Reference"/>
                          <Stat v-if="note.notes && note.notes.action3Type" :label="$t('ConversionDefinitions.ruleX', { value: 3 })" :value="getConversionRuleLabel(note.notes && note.notes.action3Type)"/>
                          <Stat v-if="note.notes && note.notes.action3Type" :label="$t('ConversionDefinitions.ruleXReference', { value: 3 })" :value="note.notes && note.notes.action3Reference"/>
                          <Stat :label="$t('ConversionDefinitions.chargeType')" :value="note.notes && note.notes.chargeType === 'P' ? $t('ConversionDefinitions.Percent') : $t('ConversionDefinitions.Fixed')"/>
                          <Stat v-if="note.notes && note.notes.chargeType === 'P'" type="percent" :label="$t('ConversionDefinitions.chargeRate')" :value="note.notes && note.notes.chargeRate / 100"/>
                          <Stat v-else type="currency" :currency="theUser ? theUser.accountCurrency: 'USD'" :label="$t('ConversionDefinitions.chargeRate')" :value="note.notes && note.notes.chargeRate"/>
                        </Stats>
                      </div>
                    </template>
                    <template v-else-if="note.objectType === 'DOMAIN' && note.objectAction === 'CREATE'">
                      {{$t('AccountNotes.addedDomain')}}: {{note.notes && note.notes.domain}}
                    </template>
                    <template v-else-if="note.objectType === 'ENDPOINT' && note.objectAction === 'CREATE'">
                      {{$t('AccountNotes.addedEndpoint')}}: {{note.objectName}}
                    </template>
                    <template v-else-if="note.objectType === 'STREAM' && note.objectAction === 'CREATE'">
                      {{$t('AccountNotes.addedStream')}}: {{note.objectName}}
                    </template>
                    <template v-else-if="note.objectType === 'CUSTOMDATA' && note.objectAction === 'CREATE'">
                      {{$t('AccountNotes.addedCustomData')}}: {{note.objectName}}
                    </template>
                    <template v-else-if="note.objectType === 'SMS' && note.objectAction === 'CREATE'">
                      {{$t('AccountNotes.addedSmsMessage')}}: {{note.objectName}}
                    </template>
                    <template v-else-if="note.objectType === 'SMS' && note.objectAction === 'DELETE'">
                      {{$t('AccountNotes.deletedSmsMessage')}}: {{note.objectName}}
                    </template>
                    <template v-else-if="note.objectType === 'SMSSETTINGS' && note.objectAction === 'CREATE'">
                      {{$t('AccountNotes.createdSmsSettings')}}
                    </template>
                    <template v-else-if="note.objectType === 'USER' && note.objectAction === 'CREATE'">
                      <template v-if="note.notes && note.notes.isAgency">
                        {{$t('AccountNotes.addedAgencyUser')}}: {{note.objectName}}
                      </template>
                      <template v-else> 
                        {{$t('AccountNotes.addedUser')}}: {{note.objectName}}
                      </template>
                    </template>
                    <template v-else-if="note.objectType === 'USER' && note.objectAction === 'DELETE'">
                      <template v-if="note.notes && note.notes.isAgency">
                        {{$t('AccountNotes.removedAgencyUser')}}: {{note.objectName}}
                      </template>
                      <template v-else> 
                        {{$t('AccountNotes.removedUser')}}: {{note.objectName}}
                      </template>
                    </template>                 
                    <template v-else-if="note.objectType === 'SHAREDRULESET'">
                      <template v-if="note.objectAction === 'CREATE'">
                        {{$t('AccountNotes.addedSharedRuleset', {value: note.objectName})}}
                      </template> 
                      <template v-else-if="note.objectAction === 'DELETE'">
                        {{$t('AccountNotes.deletedSharedRuleset', {value: note.objectName})}}
                      </template> 
                      <template v-else-if="note.objectAction === 'UPDATE'">
                        <div v-for="(noteInner, index) in note.notes" :key="index" class="simple-note-content">
                          <div class="rule">
                            <div v-if="getIsBothJson(noteInner)" class="change-rule">
                              <div class="rule-text">
                                <rule-summary :rule="getOldValueJson(noteInner)" state="removed"/>
                                <rule-summary :rule="getNewValueJson(noteInner)"/>
                              </div>
                            </div>
                            <div v-else-if="getNewValueJson(noteInner) && !getOldValueJson(noteInner)" class="new-rule">
                              <div class="rule-text"> 
                                <rule-summary :rule="getNewValueJson(noteInner)"/>
                              </div>
                            </div>
                            <div v-else-if="!getNewValueJson(noteInner) && getOldValueJson(noteInner)" class="delete-rule">
                              <div class="rule-text">
                                <rule-summary :rule="getOldValueJson(noteInner)" state="removed"/>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </template>
                    <template v-else>
                      <div v-for="(noteInner, index) in note.notes" :key="index" class="simple-note-content">
                        <template 
                          v-if="getDoAsJson(getOldValueJson(noteInner), getNewValueJson(noteInner)) 
                            && typeof getOldValueJson(noteInner) === 'object' 
                            && typeof getNewValueJson(noteInner) === 'object'"
                        >
                          <template 
                            v-if="(note.objectType === 'ENDPOINT' && note.objectAction === 'UPDATE') && (getOldValueJson(noteInner).espName !== getNewValueJson(noteInner).espName)"
                          >
                            <table class="table table-bordered">
                              <tr>
                                <td class="endpoint-table-cell">
                                  <b>{{$t('Old Value')}}</b>
                                </td>
                                <td>
                                  <b>{{$t('New Value')}}</b>
                                </td>
                              </tr>
                              <tr>
                                <td class="endpoint-table-cell">
                                  <pre>{{getOldValueJson(noteInner)}}</pre>
                                </td>
                                <td>
                                  <pre>{{getNewValueJson(noteInner)}}</pre>
                                </td>
                              </tr>
                            </table>
                          </template>
                          <template v-else>
                            <b>{{parseAttributeForTrans(noteInner.attribute)}}</b>
                            <table style="margin-top: 2px;" class="table table-bordered change-table">
                              <tr>
                                <td></td>
                                <td><b>{{$t('Old Value')}}</b></td>
                                <td><b>{{$t('New Value')}}</b></td>
                              </tr>
                              <template v-for="(key, i) of Object.keys({ ...getOldValueJson(noteInner), ...getNewValueJson(noteInner) })">
                                <tr v-if="!isEqualOldNew(getOldValueJson(noteInner)[key], getNewValueJson(noteInner)[key])" :key="i">
                                  <td>{{niceKey(key)}}</td>
                                  <td>{{getOldValueJson(noteInner)[key]}}</td>
                                  <td>{{getNewValueJson(noteInner)[key]}}</td>
                                </tr>
                              </template>
                            </table>
                          </template>
                        </template>
                        <template v-else-if="getDoAsJson(getOldValueJson(noteInner), getNewValueJson(noteInner))">
                          <b>{{parseAttributeForTrans(noteInner.attribute)}}</b>
                          <table style="margin-top: 2px;" class="table table-bordered change-table">
                            <template v-for="(key, i) of Object.keys(getNewValueJson(noteInner))">
                              <tr v-if="getNewValueJson(noteInner)[key] !== null && getNewValueJson(noteInner)[key] !== undefined" :key="i">
                                <td>{{niceKey(key)}}</td>
                                <td>{{getNewValueJson(noteInner)[key]}}</td>
                              </tr>
                            </template>
                          </table>
                        </template>
                        <template v-else-if="noteInner.newValue !== undefined">
                          <div v-html="$t('CampaignNotes.fromTo', {
                            attr: noteInner.attribute,
                            old: fixValue(noteInner.oldValue),
                            new: fixValue(noteInner.newValue),
                          })"/>
                        </template>
                        <template v-else-if="noteInner.newValue === undefined && noteInner.oldValue === undefined">
                          {{parseAttributeForTrans(noteInner.attribute)}}
                        </template>
                      </div>
                    </template>
                  </div>
                  <!-- <pre>{{note}}</pre> -->
                </div>
              </template>
            </transition-group>
          </template>
        </div>
        <div v-if="!loading && notes.length && notes.length >= limit" class="footer">
          <Button v-if="!loadingMore" :fullWidth="true" @click="loadMoreNotes">{{$t('CampaignNotes.Load More')}}</Button>
          <SkeletonBodyText v-else/>
        </div>
      </Card>
    </div>

  </Sheet>
</template>

<script>
import ScrollShadow from '../ui/ScrollShadow'

const ELEMENTS_SHOWN = 50

function parseNotes(notes) {
  const rv = []
  if (!notes) return rv
  for (const note of notes) {
    let n = note
    try {
      const obj = JSON.parse(note.notes)
      n.notes = obj
    } catch (e) {
      rv.push(note)
      continue
    }
    rv.push(n)
  }
  return rv
}
export default {
  name: 'AccountNotesSheet',
  data() {
    return {
      visible: false,
      notes: [],
      loading: true,
      loadingMore: false,
      selected: 'ALL',
      filter: '',
      newNote: '',
      conversionRules: null,
      // options: {},
      saving: false,
      // initialsColor: {},
      limit: ELEMENTS_SHOWN
    }
  },
  created() {
    this.eventHub.$on('showAccountNotes', (type = 'ALL') => {
      this.visible = true
      this.limit = ELEMENTS_SHOWN
      this.filter = ''
      this.selected = type
      this.$watch('selected', this.reloadNotes)
      NProgress.start()
      Promise.all([
        this.api.getAccountNotes(this.limit, this.selected),
        this.api.getConversionDefinitionRules(),
        this.api.getAccountRulesetsForSegmentation(),
        this.api.getSessionKey()
      ])
        .then((response) => { 
          const [notes, rules, accountRulesets, sessionKey] = response
          this.notes = parseNotes(notes.data || notes)
          this.conversionRules = (rules.data || rules).map(dr => {
            return {
              ...dr,
              label: this.$t(dr.label),
              help: this.$t(dr.help)
            }
          })
          window.RulesetEditorLoader
            .load()
            .then(lib => {
              this.$options.components.RuleSummary = lib.createRuleSummaryComponent({
                locale: this.theUser.defaultLanguage,
                authKey: sessionKey,
                campaignId: 0,
                accountRulesets
              })
              this.loading = false 
            })
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
        })
    })
  },
  beforeDestroy() {
    this.eventHub.$off('showAccountNotes')
  },
  computed: {
    tabs() {
      return [
        { id: 'ALL', label: this.$t('All') },
        { id: 'CONVERSIONDEFINITION', label: this.$t('Conversion Definition') },
        { id: 'WIDGETREVISION', label: this.$t('Widget Revision')},
        { id: 'ACCOUNT', label: this.$t('Account')},
        { id: 'ACCOUNTPREFERENCES', label: this.$t('Preferences')},
        { id: 'ACCOUNTSECURITY', label: this.$t('Security') },
        { id: 'USER', label: this.$t('Users')},
        { id: 'DOMAIN', label: this.$t('Domains') },
        { id: 'ENDPOINT', label: this.$t('Endpoints') },
        { id: 'STREAM', label: this.$t('Streams') },
        { id: 'CUSTOMDATA', label: this.$t('Custom Data') },
        { id: 'SHAREDRULESET', label: this.$t('Shared Rulesets') },
        { id: 'SMS', label: this.$t('SMS') },
        { id: 'SMSSETTINGS', label: this.$t('SMS Settings') },
      ]
    },
    filteredNotes() {
      let notes = this.notes
      if (this.selected) {
        if (this.selected !== 'ALL') {
          notes = this.notes.filter(note => note.objectType === this.selected)
        }
      }
      if (this.filter) {
        const filter = this.filter.toLowerCase()
        notes = this.notes.filter((note) => {
          if (note.createdByName.toLowerCase().indexOf(filter) > -1) {
            return true
          }
          if (typeof note.notes === 'string') {
            if (note.notes.toLowerCase().indexOf(filter) > -1) {
              return true
            }
          } else {
            if (JSON.stringify(note.notes).toLowerCase().indexOf(filter) > -1) {
              return true
            }
          }
          if (note.objectName.toLowerCase().indexOf(filter) > -1) {
            return true
          }
          return false
        })
      }
      return notes
    }
  },
  methods: {
    reloadNotes() {
      this.loading = true
      this.notes = []
      this.api.getAccountNotes(this.limit, this.selected)
        .then((response) => {
          this.notes = parseNotes(response.data || response)
          this.loading = false
        })
        .catch(this.apiCatch)
    },
    loadMoreNotes() {
      this.limit += ELEMENTS_SHOWN
      this.loadingMore = true
      this.api.getAccountNotes(this.limit, this.selected)
        .then((response) => { 
          this.notes = parseNotes(response.data || response)
          this.loadingMore = false
        })
        .catch(this.apiCatch)
    },
    getObjectLink(note) {
      if (note.objectType === 'CONVERSIONDEFINITION') {
        return `/account/conversion-definitions`
      }
      if (note.objectType === 'SHAREDRULESET' && note.objectAction !== 'DELETE') {
        return `/account/sharedRulesets/edit/${note.objectId}`
      }
      if (note.objectType === 'SMS' && note.objectAction !== 'DELETE') {
        return `/messaging/sms/edit/id/${note.objectId}`
      }
      return ''
    },
    getNoteTypeName(note) {
      if (note.objectName) return note.objectName
      if (note.objectType === 'CONVERSIONDEFINITION') {
        return this.$t('Conversion Definition')
      }
      if (note.objectType === 'WIDGETREVISION') {
        return `#${note.objectId} — ${this.$t('Widget Revision')}`
      }
      if (note.objectType === 'ACCOUNT' || note.objectType === 'ACCOUNTPREFERENCES') {
        return this.$t('Account')
      }
      if (note.objectType === 'DOMAIN') {
        return this.$t('Domain')
      }
      if (note.objectType === 'ENDPOINT') {
        return this.$t('Endpoint')
      }
      if (note.objectType === 'STREAM') {
        return this.$t('Stream')
      }
      if (note.objectType === 'CUSTOMDATA') {
        return this.$t('Custom Data')
      }
      if (note.objectType === 'SHAREDRULESET') {
        return this.$t('Shared Ruleset')
      }
      if (note.objectType === 'USER') {
        return this.$t('User')
      }
      if (note.objectType === 'SMS') {
        return this.$t('SMS')
      }
      if (note.objectType === 'SMSSETTINGS') {
        return this.$t('SMS Settings')
      }
      return this.$t('Unknown')
    },
    getNoteType(note) {
      const t = this.tabs.find(t => t.id === note.objectType)
      return t ? t.label : '?'
    },
    getConversionRuleLabel(rule) {
      const foundRule = this.conversionRules && this.conversionRules.find(r => r.value === rule)
      return foundRule && foundRule.label || ''
    },
    shouldShowDelete(note) {
      if (this.theUser && note.objectType === 'NOTES' && window.moment.tz(window.moment(), this.theUser.accountTimezone).diff(window.moment.tz(note.createdOn, this.theUser.accountTimezone)) / 1000 / 60 < 10) {
        return true
      }
      return false
    },
    isEqualOldNew(oldValue, newValue) {
      if (oldValue === null || oldValue === undefined) oldValue = ''
      if (newValue === null || newValue === undefined) newValue = ''
      return JSON.stringify(oldValue) === JSON.stringify(newValue)
    },
    parseSimpleNote(objectType, note){
      let returnNoteStr = null
      try {
        returnNoteStr = decodeURIComponent(note.replace(/\\n/g, '<br />'))
      } catch (e) {
        returnNoteStr = note.replace(/\\n/g, '<br />')
      }
      returnNoteStr = this.$t(returnNoteStr) //translates any messages coming from DB
      if (objectType === 'SHAREDRULESET') {
        returnNoteStr = returnNoteStr.replace(/^Added shared ruleset/, this.$t('CampaignNotes.Added shared ruleset'))
        returnNoteStr = returnNoteStr.replace(/^Deleted shared ruleset/, this.$t('CampaignNotes.Deleted shared ruleset'))
      } 
      return returnNoteStr
    },
    niceKey(val) {
      return val.charAt(0).toUpperCase() + val.substr(1).replace(/([A-Z])/g, ' $1')
    },
    parseAttributeForTrans(attribute) {
      return this.$t(`${attribute}`)
    },
    getDoAsJson(oldValueJson, newValueJson) {
      let doAsJson = false
      if (oldValueJson && newValueJson && (typeof oldValueJson === 'object')) {
        doAsJson = true
      }
      return doAsJson
    },
    getIsBothJson(noteInner) {
      let isBothJson = true
      try {
        JSON.parse(noteInner.oldValue)
      } catch(e) {
        isBothJson = false
      }
      try {
        JSON.parse(noteInner.newValue)
      } catch(e) {
        isBothJson = false
      }
      return isBothJson
    },
    getOldValueJson(noteInner) {
      let oldValueJson
      try {
        oldValueJson = JSON.parse(noteInner.oldValue)
      } catch (e) {
        return ''
      }
      return oldValueJson
    },
    getNewValueJson(noteInner) {
      let newValueJson
      try {
        newValueJson = JSON.parse(noteInner.newValue)
      } catch (e) {
        return ''
      }
      return newValueJson
    },
    niceRuleValue(val) {
      if (val === true) {
        return this.$t('TRUE')
      }
      if (val === false) {
        return this.$t('FALSE')
      }
      if (val === 0) {
        return '0'
      }
      if (!val) {
        return ''
      }
      return val
    },
    fixValue(value) {
      const trans = {
        'Off': this.$t('AccountNotes.off'),
        'Weekly': this.$t('AccountNotes.weekly'),
        'Monthly': this.$t('AccountNotes.monthly'),
        'Weekly and Monthly': this.$t('AccountNotes.weeklyAndMonthly')
      }
      if (value === null || value === undefined || value === '') {
        return '""'
      }
      if (typeof(value) === 'string' && trans[value]) {
        return this.$t(trans[value])
      }
      if (typeof(value) === 'object') {
        return JSON.stringify(value)
      }
      return value
    }
  },
  components: {
    ScrollShadow
  }
}
</script>

<style lang="scss">
#AccountNotesModal {
  font-size: 14px;
  .Modal-notes {
    // height: 50vh;
    overflow: hidden;
    // border: 1px solid #ccc;
    // overflow-y: scroll;
    .delete {
      margin-left: 15px;
      margin-top: -2px;
      cursor: pointer;
    }
    .note {
      hr {
        width: 80%;
        margin-top: 10px;
        margin-bottom: 10px;
      }
      .head {
        display: flex;
        align-items: center;
        background: #f5f5f5;
        padding: 10px;
        border-top: 1px solid #ddd;
        .type {
          flex: 1;
          i {
            color: #fff;
            background: #9999;
            font-size: 15px;
            width: 25px;
            height: 25px;
            border-radius: 50%;
            display: inline-block;
            padding: 5px;
            padding-left: 4px;
            margin-right: 5px;
          }
        }
        .note-info {
          color: #666;
          font-size: 80%;
          text-align: right;
        }
      }
      .details {
        margin: 15px;
        font-size: 110%;
        .inner-stats {
          margin: 10px;
        }
        .simple-note-content:first-of-type {
          margin-top: 0px;
        }
        .simple-note-content {
          margin-top: 8px;
        }
      }
    }
  }

  .footer {
    padding: 10px 15px;
    border-top: 1px solid #ccc;
  }
  .red {
    color: red;
  }

  ul > li {
    margin-top: 8px;
  }
  ul > li:first-of-type {
    margin-top: 0px;
  }

  .endpoint-table-cell {
    max-width: 296px;
    vertical-align: top;
    pre {
      overflow-x: auto;
      white-space: pre-wrap;
    }
  }

  .rule:first-of-type {
    margin-top: 5px;
  }
  .rule {
    .delete-rule {
      margin-bottom: 1px;
      background: #fef0ef;
      border-left: 4px solid #f01800;
    }
    .new-rule {
      margin-bottom: 1px;
      background: #e9f3e9;
      border-left: 4px solid green;
    }
    .change-rule {
      margin-bottom: 1px;
      background: #edf6ff;
      border-left: 4px solid #026ebb;
    }
    .rule-text {
      font-size: 14px;
      padding: 8px 16px 8px 12px;
    }
  }


  .Modal-notes-none {
    text-align: center;
    margin-top: 30px;
    margin-bottom: 30px;
    font-size: 120%;
    color: #666;
  }
  table.change-table {
    margin-top: 5px;
    // margin-left: -9px;
    font-size: 90%;
    border-radius: 0px;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1);
    td {
      padding: 3px;
      vertical-align: top;
      line-height: 22px;
      text-align: left;
    }
  }
}
</style>
